import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Entity, generateEntityId } from '../entities/entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';
import type { Worm } from '../entities/worm';
import type { CollisionMask } from '../sim/collisionMask';
import { computeSafeSpawnPosition } from './spawnHelper';

export interface HomingMissileConfig {
  x: number;
  y: number;
  vx: number;
  vy: number;
  targetX: number;
  targetY: number;
  ownerId: string;
  /** Kalsiddon: start loitering in the air (hovering) until released onto a
   *  target. While loitering the missile drifts gently and does not home. */
  loiter?: boolean;
  /** Kalsiddon: fly at a constant high speed and steer hard onto the target so
   *  it hits dead-on (vs the worm missile's gentle thrust-based homing). */
  precise?: boolean;
  /** Whether the camera should follow this missile. Default true (the worm's
   *  homing weapon); Kalsiddon's apex missiles set false so the camera stays
   *  on the tracer/bomb that spawned them. */
  track?: boolean;
}

export class HomingMissile implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2;
  target: Vector2;
  active = true;

  thrust = 280;

  explosionRadius = 45;
  maxDamage = 50;
  ownerId: string;
  rotation = 0;

  ballisticPhase = true;
  ballisticTimer = 1.0;
  homingTimer = 5.0;
  maxLifetime = 10.0;

  /** Kalsiddon: hovering near the apex until the bomb hits and releases us. */
  loitering = false;
  /** Kalsiddon: velocity-steered, constant-speed, accurate homing. */
  precise = false;
  /** Whether the camera follows this missile (false for Kalsiddon sub-missiles). */
  track = true;
  /** Circling approach offset (radians) for the legacy thrust-homing path. */
  private approachAngle = 0;

  trailPositions: Vector2[] = [];
  trailTimer = 0;

  onExplode?: (x: number, y: number) => void;
  onHomingActivated?: () => void;

  constructor(config: HomingMissileConfig) {
    this.id = generateEntityId('homing');
    this.position = { x: config.x, y: config.y };
    this.velocity = { x: config.vx, y: config.vy };
    this.target = { x: config.targetX, y: config.targetY };
    this.ownerId = config.ownerId;
    this.rotation = Math.atan2(config.vy, config.vx) + Math.PI / 2;
    this.precise = config.precise ?? false;
    this.track = config.track ?? true;
    if (config.loiter) {
      // Loitering missiles skip the ballistic phase: they hover until released.
      this.loitering = true;
      this.ballisticPhase = false;
      this.maxLifetime = 20.0; // generous - they wait for the bomb to land
    }
  }

  /** Kalsiddon: the bomb hit - converge onto the contact point (with a slight
   *  circling approach). */
  release(targetX: number, targetY: number): void {
    this.loitering = false;
    this.ballisticPhase = false;
    this.target = { x: targetX, y: targetY };
    this.homingTimer = 5.0;
  }

  update(deltaTime: number, wind = 0): void {
    if (!this.active) return;

    this.maxLifetime -= deltaTime;
    if (this.maxLifetime <= 0) {
      this.explode();
      return;
    }

    if (this.loitering) {
      // Hover near the apex: cancel most vertical drift, gentle bob, no homing.
      this.velocity.x *= 0.92;
      this.velocity.y = Math.sin(this.maxLifetime * 4) * 18;
      this.rotation += 3 * deltaTime;
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
      return;
    }

    if (this.ballisticPhase) {
      this.ballisticTimer -= deltaTime;
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      this.velocity.x += wind * 10 * deltaTime;

      if (this.ballisticTimer <= 0) {
        this.ballisticPhase = false;
        this.onHomingActivated?.();
      }
    } else if (this.precise) {
      // Kalsiddon: fly at a constant high speed and steer the velocity HEADING
      // straight at the target (turn-rate limited) so it locks on and hits
      // dead-on - a direct intercept, no swirl/sinusoid.
      const { MISSILE_SPEED, MISSILE_TURN } = WA_PHYSICS.KALSIDDON;
      const dx = this.target.x - this.position.x;
      const dy = this.target.y - this.position.y;
      const desired = Math.atan2(dy, dx);
      let cur = Math.atan2(this.velocity.y, this.velocity.x);
      // Shortest angular step toward the desired heading, capped by MISSILE_TURN.
      let diff = desired - cur;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const maxStep = MISSILE_TURN * deltaTime;
      cur += Math.max(-maxStep, Math.min(maxStep, diff));
      this.velocity.x = Math.cos(cur) * MISSILE_SPEED;
      this.velocity.y = Math.sin(cur) * MISSILE_SPEED;
      this.rotation = cur + Math.PI / 2;
    } else {
      this.homingTimer -= deltaTime;

      if (this.homingTimer <= 0) {
        this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime * 0.5;
      } else {
        const dx = this.target.x - this.position.x;
        const dy = this.target.y - this.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 5) {
          // Circling approach: bias the thrust direction by a decaying angular
          // offset so the missile spirals into the contact point a little
          // instead of diving dead-straight. The offset shrinks as it nears.
          this.approachAngle += 7 * deltaTime;
          const swirl = Math.min(1, dist / 160) * 0.7 * Math.sin(this.approachAngle);
          const targetAngle = Math.atan2(dy, dx) + swirl;
          this.rotation = targetAngle + Math.PI / 2;

          this.velocity.x += Math.cos(targetAngle) * this.thrust * deltaTime;
          this.velocity.y += Math.sin(targetAngle) * this.thrust * deltaTime;
        }
      }
    }

    if (this.ballisticPhase) {
      this.rotation = Math.atan2(this.velocity.y, this.velocity.x) + Math.PI / 2;
    }

    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;

    this.trailTimer += deltaTime;
    if (this.trailTimer >= 0.02) {
      this.trailTimer = 0;
      this.trailPositions.push({ x: this.position.x, y: this.position.y });
      if (this.trailPositions.length > 20) {
        this.trailPositions.shift();
      }
    }
  }

  checkTerrainCollision(terrain: CollisionMask): boolean {
    if (!this.active) return false;
    return terrain.isSolid(this.position.x, this.position.y);
  }

  checkWormCollision(worms: Worm[]): Worm | null {
    for (const worm of worms) {
      if (worm.state === 'dead') continue;

      const box = worm.getBounds();
      if (
        this.position.x >= box.x &&
        this.position.x <= box.x + box.width &&
        this.position.y >= box.y &&
        this.position.y <= box.y + box.height
      ) {
        return worm;
      }
    }
    return null;
  }

  checkWaterCollision(waterLevel: number, worldHeight: number): boolean {
    return this.position.y > worldHeight - waterLevel;
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x, this.position.y);
  }

  destroy(): void {
    this.active = false;
  }

  getRotation(): number {
    return this.rotation;
  }
}

export const WeaponHomingMissile: WeaponDefinition = {
  id: 'homing',
  name: 'Homing Missile',
  icon: 'homing.png',
  category: 'homing',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: false,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, target, world } = ctx;

    if (!target) {
      return {
        success: false,
        message: 'Select a target location first',
      };
    }

    const normalizedPower = power / 100;
    const speed = Math.log(normalizedPower * 9 + 1) * 300;
    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

    const spawnDistance = 25;
    const spawn = computeSafeSpawnPosition(worm, aimRad, spawnDistance, world);

    const missile = new HomingMissile({
      x: spawn.x,
      y: spawn.y,
      vx: Math.cos(aimRad) * speed,
      vy: -Math.sin(aimRad) * speed,
      targetX: target.x,
      targetY: target.y,
      ownerId: worm.id,
    });

    return {
      success: true,
      entities: [missile as unknown as Entity],
    };
  },
};
